import React, { useState, useEffect, useContext } from 'react'
import { Button, FormControl, Modal } from 'react-bootstrap'

import FilterDropdown from 'live/components/forms/ui/filter-dropdown'
import LiveSelect from 'live/components/forms/ui/select'
import authorization from 'live/services/authorization'
import globalNotification from 'live/services/global-notification'
import i18n from 'live/services/i18n'

import { DraExportData, DraExportDataXLS } from 'plugin-codaf-risk-ext-bowtie/services/export'
import { CustomList, RemoveConfirmationModal } from 'plugin-generic-components/shared/components/components'

import { FilteredSearch, ImportDataModal, IncompleteExportConfimationModal, RoundedModal } from '../../../generic'
import { associateBowtieData, getOperations, listAssets } from '../../../services'
import { listBowties, deleteBowtie /* , createBowtie */ } from '../../../services/bowtie'
import { IBowtie, IOperation, IPaginatedREST } from '../../../types'
import { PAGINATION_SIZE } from '../../../utils/constants'
import { BowtieSettingsRouteContext, IBowtieSettingsRouteContext, IQueryParamsList } from '../../BowtieSettingWrapper'
import { AddBowtieModal } from './components'
import { ImportBowtieModal } from './components/ImportBowtieModal/ImportBowtieModal'
import { BowtieListContainer } from './styles'

export enum StatusStage {
    START = 'START',
    END = 'END',
    ERROR = 'ERROR',
    RD = 'RISKDIAGRAM',
    BARRIER = 'BARRIER',
    BOWTIE = 'BOWTIE'
}

export interface IImportStatus {
    stage: string
    current?: number
    total?: number
    errorMessage?: string
    statusDisplay?: string
}

export const BowtieList: React.FC = () => {
    const [bowtieRequest, setBowtieRequest] = useState<IPaginatedREST<IBowtie>>({
        total: 0,
        totalPages: 0,
        data: [],
        index: 0,
        size: 0
    })
    const [operations, setOperations] = useState<IOperation[]>([])
    const [showAddBowtieModal, setShowAddBowtieModal] = useState(false)
    const [showRemoveBowtieModal, setShowRemoveBowtieModal] = useState(false)
    const [showAssociateModal, setShowAssociateModal] = useState(false)
    const [tempBowtie, setTempBowtie] = useState<IBowtie>()
    const [loading, setLoading] = useState<boolean>(false)
    const { params, setParams } = useContext<IBowtieSettingsRouteContext<IQueryParamsList>>(BowtieSettingsRouteContext)
    const [assetList, setAssetList] = useState([])
    const [selectOperationFilter, setSelectOperationFilter] = useState(null)
    const [selectAssetFilter, setSelectAssetFilter] = useState(null)
    const [showIncompleteExportConfimationModal, setShowIncompleteExportConfimationModal] = useState(false)
    const [exportImportLoading, setExportImportLoading] = useState<boolean>(false)
    const [files, setFiles] = useState<FileList>(null)
    const [isImporting, setIsImporting] = useState<boolean>(false)

    const [importStatus, setImportStatus] = useState<IImportStatus>()

    const bowtieAdmin = authorization.superAdmin || authorization.Permission.BOWTIE_ADMIN
    const bowtieManager = authorization.Permission.BOWTIE_MANAGER

    const handleGetBowtiesList = async (): Promise<void> => {
        try {
            setLoading(true)
            const response = await listBowties(params)
            setBowtieRequest(response)
        } catch (err) {
            globalNotification.error(err.message ?? i18n('An error occurred on list bowties.'))
        } finally {
            setLoading(false)
        }
    }

    const handleCloseProtocol = (reload: boolean): void => {
        if (reload) {
            handleGetBowtiesList()
        }
        setShowAssociateModal(false)
        setFiles(null)
        setIsImporting(false)
        setImportStatus(null)
    }

    const handleGetOperations = async (): Promise<void> => {
        try {
            const operationsFetch = await getOperations()
            setOperations(operationsFetch.data)
        } catch (err) {
            globalNotification.error(err.message ?? i18n('An error occurred on get operations.'))
        }
    }

    const handleGetAssetList = async (): Promise<void> => {
        listAssets().then(resp => setAssetList(resp.data))
    }

    const handleExportData = async (): Promise<void> => {
        try {
            setExportImportLoading(true)
            await DraExportDataXLS({ entityType: 'bowtie', acceptEmptyArguments: 'false' })
        } catch (error) {
            if (error.message.toLowerCase().includes('there is at least one')) {
                setShowIncompleteExportConfimationModal(true)
                globalNotification.error(i18n('Confirm incomplete export'))
            } else {
                globalNotification.error(error.message ?? i18n('An error occurred on exporting Bowtie data.'))
                console.log(error)
            }
        } finally {
            setExportImportLoading(false)
        }
    }

    const handleSearch = async (keyword?: string): Promise<void> => {
        let operation = ''
        let asset = ''
        if (selectOperationFilter && selectOperationFilter.id) {
            operation = selectOperationFilter.id
        }
        if (selectAssetFilter && selectAssetFilter.id) {
            asset = selectAssetFilter.id
        }
        if (keyword !== undefined) {
            setParams({ ...params, keyword, operation, asset })
        } else {
            setParams({ ...params, operation, asset })
        }
    }

    const handleSortList = async (orderBy: string, direction: 'ASC' | 'DESC'): Promise<void> => {
        setParams({ ...params, orderBy, direction })
    }

    const handlePerPageChange = async (perPage: number): Promise<void> => {
        setParams({ ...params, perPage })
    }

    const handleNewBowtie = (): void => {
        setShowAddBowtieModal(true)
    }

    const handleDeleteBowtie = (id: number): void => {
        setTempBowtie(bowtieRequest.data.find(b => b.id === id))
        setShowRemoveBowtieModal(true)
    }

    const handleCloseDeleteBowtie = (): void => {
        handleGetBowtiesList()
        setShowRemoveBowtieModal(false)
        setTempBowtie(null)
    }

    const handleCloseAddBowtieModal = (): void => {
        setShowAddBowtieModal(false)
        setTempBowtie(null)
    }

    const clearSelectFilters = (): void => {
        setSelectAssetFilter(null)
        setSelectOperationFilter(null)
    }

    useEffect(() => {
        handleGetBowtiesList()
    }, [params]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        handleGetOperations()
        handleGetAssetList()
    }, [])

    return (
        <BowtieListContainer>
            <div className="first-row-container">
                {bowtieAdmin && (
                    <Button
                        className="no-outline-button"
                        bsStyle="primary"
                        onClick={handleNewBowtie}
                        style={{ borderRadius: 3, marginRight: 8 }}
                        id="bowtieList-list-button-newBowtie"
                    >
                        <i className="far fa-plus custom-plus" /> {i18n('New Bowtie')}
                    </Button>
                )}
                <FilteredSearch
                    keyword={params.keyword}
                    inputId="bowtieList-list-field-searchBowtie"
                    selectedFilterOption={operations.findIndex(o => o.id === params.filterBy) + 1}
                    onSearch={handleSearch}
                />
                <FilterDropdown
                    className="dropdown"
                    btnLabel={
                        <span>
                            <i className="far fa-filter no-margin" />
                            <span className="caret" />
                        </span>
                    }
                >
                    <li className="menu-item">
                        <LiveSelect
                            label={i18n('Resource')}
                            placeholder={i18n('Select...')}
                            showOptionAll={true}
                            options={assetList.map(asset => {
                                asset.value = asset.id
                                asset.label = asset.name

                                return asset
                            })}
                            onChange={(_, item) => {
                                setSelectAssetFilter(item)
                            }}
                            value={selectAssetFilter}
                        />
                    </li>
                    <li className="menu-item">
                        <LiveSelect
                            label={i18n('Operation')}
                            placeholder={i18n('Select...')}
                            showOptionAll={true}
                            options={operations.map(op => {
                                op.value = op.id
                                op.label = i18n(op.description)

                                return op
                            })}
                            onChange={(_, item) => {
                                setSelectOperationFilter(item)
                            }}
                            value={selectOperationFilter}
                        />
                    </li>
                    <li className="menu-item">
                        <Button
                            bsSize="small"
                            bsStyle="primary"
                            onClick={() => {
                                handleSearch()
                            }}
                        >
                            {i18n('Apply')}
                        </Button>
                        <Button
                            bsSize="small"
                            bsStyle="link"
                            onClick={() => {
                                clearSelectFilters()
                            }}
                        >
                            {i18n('Restore Defaults')}
                        </Button>
                    </li>
                </FilterDropdown>
                {(bowtieAdmin || bowtieManager) && (
                    <Button
                        className="export-import-button"
                        onClick={() => {
                            handleExportData()
                        }}
                        id="export-data"
                    >
                        {exportImportLoading ? (
                            <i className="fa fa-spinner fa-spin fa-x2" />
                        ) : (
                            <>
                                <i className="fas fa-download" /> {i18n('Export data')}
                            </>
                        )}
                    </Button>
                )}
                {bowtieAdmin && (
                    <Button
                        className="export-import-button"
                        onClick={() => {
                            setShowAssociateModal(old => !old)
                        }}
                    >
                        {exportImportLoading ? (
                            <i className="fa fa-spinner fa-spin fa-x2" />
                        ) : (
                            <>
                                <i className="fas fa-upload" /> {i18n('Import data')}
                            </>
                        )}
                    </Button>
                )}
            </div>
            {loading ? (
                <div className="loading-spin">
                    <i className="fa fa-spinner fa-spin fa-x2" />
                </div>
            ) : bowtieRequest.data && bowtieRequest.data.length ? (
                <CustomList
                    label="Bowties"
                    list={bowtieRequest.data.map(bow => ({
                        id: bow.id,
                        asset: bow.asset.name,
                        hazard: bow.topEvent.hazard.name,
                        topEvent: bow.topEvent.name,
                        operation: bow.topEvent.hazard.operation.description,
                        bowtieCode: bow.bowtieCode
                    }))}
                    columns={[
                        // { title: 'ID', listRef: 'id', sortable: true },
                        { title: 'BOWTIE CODE', listRef: 'bowtieCode', sortable: true },
                        { title: 'RESOURCE', listRef: 'asset', sortable: true },
                        { title: 'HAZARD', listRef: 'hazard', sortable: true },
                        { title: 'TOP EVENT', listRef: 'topEvent', sortable: true },
                        { title: 'OPERATION', listRef: 'operation', sortable: true }
                    ]}
                    sortField={params.orderBy === 'top_event' ? 'topEvent' : params.orderBy}
                    onSortClick={handleSortList}
                    sortDirection={params.direction}
                    tableProps={{ style: { width: '100%' } }}
                    configOptions={
                        bowtieAdmin
                            ? [
                                  {
                                      icon: 'far fa-edit',
                                      onClickConfig: k => setParams({ type: 'edit', tab: 'bowties', id: k.id })
                                  },
                                  // { icon: 'far fa-copy', onClickConfig: k => handleCopy(k.id) },
                                  { icon: 'far fa-trash-alt', onClickConfig: k => handleDeleteBowtie(k.id) }
                              ]
                            : [
                                  {
                                      icon: 'fas fa-eye',
                                      onClickConfig: k => setParams({ type: 'edit', tab: 'bowties', id: k.id })
                                  }
                              ]
                    }
                    pagination={{
                        type: 'live',
                        totalItems: bowtieRequest.total,
                        showingItems: bowtieRequest.size,
                        onPerPageChange: () => handlePerPageChange((params.perPage += PAGINATION_SIZE))
                    }}
                    fixedHeader={true}
                />
            ) : (
                <div className="no-bowtie">
                    <i className="far fa-folder-open" />
                    <p>{i18n('There are no Bowties yet')}</p>
                    <a onClick={handleNewBowtie}>{i18n('Create new Bowtie')}</a>
                </div>
            )}

            <AddBowtieModal
                handleClose={handleCloseAddBowtieModal}
                handleSave={id => setParams({ type: 'edit', tab: 'bowties', id: id })}
                show={showAddBowtieModal}
                operations={operations}
                item={tempBowtie}
            />

            <RemoveConfirmationModal
                handleClose={handleCloseDeleteBowtie}
                show={showRemoveBowtieModal}
                setShow={setShowRemoveBowtieModal}
                onDelete={async () => {
                    await deleteBowtie(tempBowtie.id)
                    handleCloseDeleteBowtie()
                }}
                type="bowtie"
                removalMessage={`${i18n('Are you sure you want remove the bowtie')} ${tempBowtie?.id}?`}
            />
            <ImportBowtieModal
                handleClose={handleCloseProtocol}
                show={showAssociateModal}
                files={files}
                setFiles={setFiles}
                isImporting={isImporting}
                setIsImporting={setIsImporting}
                handleGetBowtiesList={handleGetBowtiesList}
                importStatus={importStatus}
                setImportStatus={setImportStatus}
            />
            <IncompleteExportConfimationModal
                setLoading={setExportImportLoading}
                show={showIncompleteExportConfimationModal}
                handleClose={() => setShowIncompleteExportConfimationModal(false)}
                type={'bowtie'}
            />
        </BowtieListContainer>
    )
}
